import { useEffect, useState } from "react";
import { formatPot } from "@unfaithful/client";
import {
  goldFor,
  PLAY_INFO,
  POTION_MAX_DROPS,
  SEAL_TURN_MS,
  SORT_GROUPS,
  TIER_INFO,
  tierFor,
  TWIST_INFO,
  ROOM_SLOTS,
  slotBox,
  LINK_COLOURS,
  type GameActionInput,
  type PlayerView,
  type PlayInputInput,
  type PublicQuestionCard,
} from "@unfaithful/shared";
import "./play.css";
import { phoneSound, type PhoneSound } from "./sound";
import { DrawingPad } from "./DrawingPad";
import { RoomPicture } from "./RoomPicture";
import { LinkPad } from "./LinkPad";

const LETTERS = ["A", "B", "C", "D", "E", "F"];
/** Fog twist: a Speed Lock digit fades this long after it's revealed. */
const FOG_MS = 6000;

/**
 * A phone mission in your hand: the brief, your private part, and the one
 * thing you can do right now. Every phone shows the same layout for the same
 * job, so nobody's screen gives away their role.
 */
export function PlayPanel({
  view,
  clockOffset,
  run,
}: {
  view: PlayerView;
  clockOffset: number;
  run: (action: GameActionInput) => Promise<void>;
}) {
  const { castle, me, secrets } = view;
  const play = castle.game!.play!;
  const mine = secrets.play;
  const info = PLAY_INFO[play.kind];
  const now = useNow(clockOffset);
  const left = play.stageEndsAt === null ? null : Math.max(0, Math.ceil((play.stageEndsAt - now) / 1000));
  const send = (input: PlayInputInput) => run({ type: "mission-input", input } as GameActionInput);
  const byId = (id: string) => castle.players.find((p) => p.id === id);
  // The whole table's gold so far: everyone's phone shows the same pot rising.
  const teamGold = goldFor(play.potValue, play.score ?? play.progress);

  const head = (
    <header className="pm-head">
      <span>
        {info.title}
        {play.steps > 1 && play.stage !== "brief" && ` · ${play.step + 1} of ${play.steps}`}
      </span>
      {play.stage === "play" && left !== null && <span className={`pm-clock ${left <= 10 ? "is-urgent" : ""}`}>{left}s</span>}
      {(play.stage === "play" || play.stage === "step-result") && play.kind !== "sort-it" && play.kind !== "spot-the-change" && (
        <span className="pm-team" aria-label="The table's gold so far">
          <span className="pm-team-bar">
            <span style={{ width: `${Math.max(0, Math.min(100, play.score ?? play.progress))}%` }} />
          </span>
          Together: {formatPot(teamGold)} of {formatPot(goldFor(play.potValue, 100))}
        </span>
      )}
    </header>
  );

  if (play.stage === "brief") {
    const ready = play.ready.includes(me.id);
    return (
      <div className="pm">
        <p className="pm-title">{info.title}</p>
        <p className="quiet">{info.goal}</p>
        <ol className="pm-how">
          {info.how.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ol>
        {play.twist && (
          <p className="pm-twist">
            <span className="pm-badge">{TWIST_INFO[play.twist].badge}</span> {TWIST_INFO[play.twist].line}
          </p>
        )}
        <button className="secondary moon-button" disabled={ready} onClick={() => void send({ k: "ready" })}>
          {ready ? "Ready. Waiting for the others" : "Ready"}
        </button>
      </div>
    );
  }

  if (play.stage === "result" || play.stage === "done") {
    const score = play.score ?? 0;
    const tier = tierFor(score);
    return (
      <div className="pm">
        {head}
        <p className={`pm-verdict tier-${tier}`}>{TIER_INFO[tier].label}</p>
        <p className="panel-lead">{goldFor(play.potValue, score) > 0 ? `+${formatPot(goldFor(play.potValue, score))} for the pot.` : "Nothing for the pot."}</p>
        <p className="quiet">Eyes on the TV.</p>
      </div>
    );
  }

  switch (play.kind) {
    case "speed-lock": {
      const sl = mine?.speedLock;
      if (!sl) return null;
      return (
        <div className="pm">
          {head}
          {sl.position !== null && <p className="pm-role">You hold digit {sl.position + 1}.</p>}
          {play.stage === "reveal" ? (
            <p className="quiet">{sl.isKeyholder ? "You're also the Keyholder: you'll type the code." : "Your puzzle appears in a moment."}</p>
          ) : (
            <>
              {sl.status === "open" && sl.puzzle && (
                <Question
                  q={sl.puzzle}
                  submitLabel="Crack it"
                  note={sl.puzzle.options ? "One go only." : `${3 - sl.tries} ${3 - sl.tries === 1 ? "try" : "tries"} left.`}
                  onAnswer={(a) => void send({ k: "answer", ...a })}
                  locked={false}
                />
              )}
              {sl.status === "solved" && sl.digit !== null && (
                <Digit digit={sl.digit} fog={play.twist === "fog"} solvedAt={sl.solvedAt} now={now} />
              )}
              {sl.status === "burnt" && <p className="panel-lead">Your puzzle is spent. Help the others.</p>}
              {sl.isKeyholder && <Keypad digits={castle.game!.play!.speedLock!.digits} code={castle.game!.play!.speedLock!.code} send={send} />}
            </>
          )}
        </div>
      );
    }

    case "closest-guess": {
      const q = play.closestGuess?.question;
      const my = mine?.closestGuess?.myGuess ?? null;
      if (play.stage === "step-result") {
        const r = play.closestGuess?.reveal;
        return (
          <div className="pm">
            {head}
            <p className="panel-lead">Answer: {r?.answer.toLocaleString("en-GB")}</p>
            <p className="quiet">The table said {r?.median?.toLocaleString("en-GB") ?? "nothing"}. You said {my?.toLocaleString("en-GB") ?? "nothing"}.</p>
          </div>
        );
      }
      return (
        <div className="pm">
          {head}
          {q && <p className="pm-prompt">{q.prompt}</p>}
          {my !== null ? (
            <p className="pm-locked">You guessed {my.toLocaleString("en-GB")}{q?.unit ? ` ${q.unit}` : ""}. Wait for the others.</p>
          ) : (
            <NumberEntry unit={q?.unit} onSubmit={(value) => void send({ k: "guess", value })} />
          )}
          <p className="pm-small">The middle guess at the table counts, so talk it through.</p>
        </div>
      );
    }

    case "sort-it": {
      const tiles = play.sortIt!.tiles;
      const myTiles = mine?.sortIt?.myTiles ?? [];
      const allPlaced = myTiles.every((i) => tiles[i]?.group !== null);
      const ready = play.ready.includes(me.id);
      return (
        <div className="pm">
          {head}
          {play.stage === "reveal" ? (
            <p className="panel-lead">You hold {myTiles.length} {myTiles.length === 1 ? "tile" : "tiles"}. The rest are on other phones.</p>
          ) : (
            <>
              <p className="quiet">Agree the groups out loud, then give each of your tiles a colour. The TV shows everyone's.</p>
              <ul className="pm-tiles">
                {myTiles.map((i) => (
                  <li key={i} className="pm-tile">
                    <span className="pm-tile-text">{tiles[i]!.text}</span>
                    <span className="pm-groups" role="radiogroup" aria-label={`Group for ${tiles[i]!.text}`}>
                      {SORT_GROUPS.map((g, gi) => (
                        <button
                          key={g.name}
                          role="radio"
                          aria-checked={tiles[i]!.group === gi}
                          aria-label={g.name}
                          className={`pm-group ${tiles[i]!.group === gi ? "is-on" : ""}`}
                          style={{ "--group": g.colour } as React.CSSProperties}
                          onClick={() => void send({ k: "tile", tile: i, group: tiles[i]!.group === gi ? null : gi })}
                        >
                          {g.shape}
                        </button>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
              <button className="primary wax-button" disabled={!allPlaced || ready} onClick={() => void send({ k: "ready" })}>
                {ready ? "Happy. Waiting for the others" : "We're done"}
              </button>
            </>
          )}
        </div>
      );
    }

    case "potion-mixer": {
      const pm = play.potionMixer!;
      const mine = secrets.play?.potionMixer;
      const ing = mine?.ingredient != null ? pm.ingredients[mine.ingredient] : undefined;
      if (!ing) return <div className="pm">{head}<p className="quiet">Watch the flasks on the TV and help the others.</p></div>;
      const drops = mine?.myDrops ?? 0;
      const ready = play.ready.includes(me.id);
      const pour = (n: number) => void send({ k: "drops", value: Math.max(0, Math.min(POTION_MAX_DROPS, n)) });
      return (
        <div className="pm">
          {head}
          <p className="pm-role">You pour {ing.name}.</p>
          {mine?.scrap && pm.ingredients[mine.scrap.ingredient] && (
            <div className="pm-scrap" style={{ "--ing": pm.ingredients[mine.scrap.ingredient]!.colour } as React.CSSProperties}>
              <p className="pm-scrap-title">Your scrap of the recipe</p>
              <p className="pm-scrap-line">
                <span className="pm-scrap-dot" aria-hidden /> {pm.ingredients[mine.scrap.ingredient]!.name}: <strong>{mine.scrap.drops} drops</strong> in all
              </p>
              <p className="pm-small">Not yours to pour. Tell whoever pours {pm.ingredients[mine.scrap.ingredient]!.name}. Someone else holds your number.</p>
            </div>
          )}
          <div className="pm-ingredient" style={{ "--ing": ing.colour } as React.CSSProperties}>
            <span className="pm-bottle" aria-hidden />
            <span className="pm-drops">
              {drops} <span>{drops === 1 ? "drop" : "drops"}</span>
            </span>
          </div>
          {play.stage === "play" && (
            <>
              <div className="pm-pour">
                <button className="pm-key" onClick={() => pour(drops - 1)} disabled={drops === 0} aria-label="One drop less">
                  −
                </button>
                <input
                  className="pm-slider"
                  type="range"
                  min={0}
                  max={POTION_MAX_DROPS}
                  value={drops}
                  onChange={(e) => pour(Number(e.target.value))}
                  aria-label={`Drops of ${ing.name}`}
                  style={{ "--ing": ing.colour } as React.CSSProperties}
                />
                <button className="pm-key" onClick={() => pour(drops + 1)} disabled={drops === POTION_MAX_DROPS} aria-label="One drop more">
                  +
                </button>
              </div>
              <p className="pm-small">
                {ing.owners.length > 1 ? `You share ${ing.name} with ${ing.owners.length - 1} other${ing.owners.length > 2 ? "s" : ""}: split the drops between you. ` : ""}
                Watch the brew change on the TV.
              </p>
              <button className="primary wax-button" disabled={ready} onClick={() => void send({ k: "ready" })}>
                {ready ? "Sealed. Waiting for the others" : "Seal the potion"}
              </button>
            </>
          )}
        </div>
      );
    }

    case "portrait-gallery": {
      const pg = play.portrait!;
      const mine = secrets.play?.portrait;
      const artistName = castle.players.find((p) => p.id === pg.artist)?.name ?? "The artist";
      if (play.stage === "step-result") {
        return (
          <div className="pm">
            {head}
            <p className="pm-role">It was: {pg.word}</p>
            <p className="quiet">{mine?.isArtist ? `${pg.solved.length} of you got it.` : mine?.solved ? "You got it." : "You missed it."}</p>
          </div>
        );
      }
      if (mine?.isArtist) {
        return (
          <div className="pm">
            {head}
            <p className="pm-role">
              Draw: <span className="pm-word">{mine.word}</span>
            </p>
            <p className="pm-small">No letters or numbers. Screen to yourself.</p>
            <DrawingPad
              strokes={pg.strokes}
              enabled={play.stage === "play"}
              onStroke={(st) => void send({ k: "stroke", points: st.points, ink: st.ink })}
              onWipe={() => void send({ k: "wipe" })}
            />
            <p className="pm-small">{pg.solved.length} got it so far.</p>
          </div>
        );
      }
      return (
        <div className="pm">
          {head}
          <p className="pm-role">{mine?.solved ? "You got it. Keep it to yourself." : `${artistName} is drawing. What is it?`}</p>
          <DrawingPad strokes={pg.strokes} enabled={false} />
          {!mine?.solved && <WordGuess letters={pg.letters} onGuess={(text) => void send({ k: "word", text })} />}
          {(mine?.myGuesses.length ?? 0) > 0 && !mine?.solved && <p className="pm-small">Tried: {mine!.myGuesses.slice(-4).join(", ")}</p>}
        </div>
      );
    }

    case "link-up": {
      const lu = play.linkUp!;
      const mine = lu.owners.map((o, i) => (o.includes(me.id) ? i : -1)).filter((i) => i >= 0);
      const names = mine.map((i) => `${LINK_COLOURS[i]!.name} ${LINK_COLOURS[i]!.shape}`);
      if (play.stage === "reveal") {
        return (
          <div className="pm">
            {head}
            <p className="pm-role">{mine.length === 0 ? "No colour for you this time. Help the others." : `Your ${mine.length > 1 ? "colours" : "colour"}: ${names.join(" and ")}`}</p>
            <p className="quiet">Drag from a lantern to its twin. Talk it through: paths can't cross, and drawing over someone's path cuts it.</p>
          </div>
        );
      }
      const done = play.ready.includes(me.id);
      const joined = lu.connected.filter(Boolean).length;
      return (
        <div className="pm">
          {head}
          <p className="pm-role">{mine.length > 0 ? `You draw ${names.join(" and ")}.` : "Help the others find their routes."}</p>
          <LinkPad lu={lu} meId={me.id} onPath={(colour, cells) => void send({ k: "path", colour, cells })} />
          <p className="pm-small">
            {joined} of {lu.ends.length} joined. {lu.filled} of {lu.size * lu.size} squares filled.
          </p>
          <button className="secondary moon-button" disabled={done} onClick={() => void send({ k: "ready" })}>
            {done ? "Done. Waiting for the others" : "We're finished"}
          </button>
        </div>
      );
    }

    case "spot-the-change": {
      const sp = play.spot!;
      if (play.stage === "reveal") {
        return (
          <div className="pm">
            {head}
            <p className="pm-watch">Look at the TV</p>
            <p className="panel-lead">Remember everything in the room. In a moment, {sp.changes} things will change.</p>
          </div>
        );
      }
      const done = play.ready.includes(me.id);
      const nameOf = (id: string) => castle.players.find((p) => p.id === id)?.name ?? "Someone";
      return (
        <div className="pm">
          {head}
          <p className="pm-role">Tap what changed. Tap again to rub it out.</p>
          <div className="pm-room">
            <RoomPicture scene={sp.scene} label="The room now. Tap a place to circle it.">
              {sp.marks.map((m) => {
                const b = slotBox(ROOM_SLOTS[m.slot]!);
                return (
                  <ellipse
                    key={m.slot}
                    className={`pm-circle ${m.by === me.id ? "is-mine" : ""}`}
                    cx={b.x + b.w / 2}
                    cy={b.y + b.h / 2}
                    rx={b.w / 2 + 12}
                    ry={b.h / 2 + 12}
                  />
                );
              })}
              {ROOM_SLOTS.map((slot, i) => {
                const b = slotBox(slot);
                const circled = sp.marks.find((m) => m.slot === i);
                return (
                  <rect
                    key={i}
                    className="pm-spot"
                    x={b.x - 8}
                    y={b.y - 8}
                    width={b.w + 16}
                    height={b.h + 16}
                    role="button"
                    tabIndex={0}
                    aria-label={circled ? `Place ${i + 1}, circled by ${nameOf(circled.by)}` : `Place ${i + 1}`}
                    onClick={() => void send({ k: "mark", slot: i })}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && void send({ k: "mark", slot: i })}
                  />
                );
              })}
            </RoomPicture>
          </div>
          <p className="pm-small">
            Swipe to look around. {sp.marks.length} of {sp.maxMarks} circles used.
          </p>
          <button className="secondary moon-button" disabled={done} onClick={() => void send({ k: "ready" })}>
            {done ? "Done. Waiting for the others" : "We've found them all"}
          </button>
        </div>
      );
    }

    case "seal-pairs": {
      const sp = play.sealPairs!;
      const stale = sp.turnStartedAt !== null && now - sp.turnStartedAt > SEAL_TURN_MS;
      const myTurn = sp.turn === me.id || stale;
      const turnName = castle.players.find((p) => p.id === sp.turn)?.name ?? "Someone";
      return (
        <div className="pm">
          {head}
          <p className="pm-role">
            {sp.turn === me.id ? (sp.pending === null ? "Your turn. Flip a seal." : "Now flip its twin.") : stale ? `${turnName} is slow. Flip one to take the turn.` : `${turnName}'s turn. Help them remember.`}
          </p>
          <ol className="pm-seals">
            {sp.faces.map((face, i) => (
              <li key={i}>
                <button
                  className={`pm-seal ${face ? "is-open" : ""} ${sp.matched[i] ? "is-matched" : ""}`}
                  disabled={!myTurn || sp.matched[i] || sp.pending === i || play.stage !== "play"}
                  onClick={() => void send({ k: "flip", cell: i })}
                  aria-label={face ? `Seal ${i + 1}, ${face}` : `Seal ${i + 1}`}
                >
                  {face ?? i + 1}
                </button>
              </li>
            ))}
          </ol>
          <p className="pm-small">{sp.pairs} of {sp.faces.length / 2} pairs found.</p>
        </div>
      );
    }

    case "blackout": {
      const b = play.blackout!;
      const flashing = !!b.flash && b.flashEndsAt !== null && now < b.flashEndsAt;
      if (play.stage === "step-result") {
        const right = b.reveal?.right.includes(me.id);
        return (
          <div className="pm">
            {head}
            <SoundOnce kind={b.reveal?.teamRight ? "right" : "wrong"} />
            <p className={`pm-verdict ${b.reveal?.teamRight ? "tier-strong" : "tier-failure"}`}>
              {b.reveal?.team === null ? "The table was split. No answer counts." : b.reveal?.teamRight ? "The table got it." : "The table got it wrong."}
            </p>
            <p className="panel-lead">Answer: {b.reveal?.answerText}</p>
            <p className="pm-small">{right ? "You saw it too." : "You missed this one."}</p>
          </div>
        );
      }
      if (flashing) {
        return (
          <div className="pm">
            {head}
            <p className="pm-watch">Watch the TV.</p>
          </div>
        );
      }
      const a = mine?.blackout?.myAnswer ?? null;
      return (
        <div className="pm">
          {head}
          {b.question?.options ? (
            <TeamPick q={b.question} mine={a?.option ?? null} tally={b.tally} byName={(id) => byId(id)?.name ?? "?"} onPick={(option) => {
                phoneSound("lock");
                void send({ k: "answer", option, text: null });
              }} />
          ) : (
            b.question && <Question q={b.question} locked={a !== null} chosen={a} submitLabel="Lock it in" note="The most popular answer at the table is the one that counts." onAnswer={(x) => void send({ k: "answer", ...x })} />
          )}
        </div>
      );
    }
  }
}

/**
 * One answer for the whole table: tap to pick, tap another to change your
 * mind, and see who is saying what. The most popular answer counts.
 */
function TeamPick({
  q,
  mine,
  tally,
  byName,
  onPick,
}: {
  q: PublicQuestionCard;
  mine: number | null;
  tally: { label: string; ids: string[] }[];
  byName: (id: string) => string;
  onPick: (option: number) => void;
}) {
  return (
    <>
      <p className="pm-prompt">{q.prompt}</p>
      <ol className="pm-options">
        {q.options!.map((o, i) => {
          const who = tally.find((t) => t.label === o)?.ids ?? [];
          return (
            <li key={i}>
              <button className={`pm-option ${mine === i ? "is-chosen" : ""}`} aria-pressed={mine === i} onClick={() => onPick(i)}>
                <span className="pm-letter">{LETTERS[i]}</span>
                <span className="pm-option-text">
                  {o}
                  {who.length > 0 && <span className="pm-who">{who.map(byName).join(", ")}</span>}
                </span>
                {who.length > 0 && <span className="pm-count">{who.length}</span>}
              </button>
            </li>
          );
        })}
      </ol>
      <p className="pm-small">One answer for the table: the most popular counts, and a split table scores nothing. Change your pick until everyone's in.</p>
    </>
  );
}

/** Plays a sound once, when this part of the screen first appears. */
function SoundOnce({ kind }: { kind: PhoneSound }) {
  useEffect(() => phoneSound(kind), [kind]);
  return null;
}

function WordGuess({ letters, onGuess }: { letters: number; onGuess: (t: string) => void }) {
  const [text, setText] = useState("");
  const go = () => {
    if (!text.trim()) return;
    onGuess(text);
    setText("");
  };
  return (
    <div className="pm-number">
      <input
        className="text-input pm-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={40}
        placeholder={`${letters} letters`}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="send"
        onKeyDown={(e) => e.key === "Enter" && go()}
      />
      <button className="primary wax-button pm-send" disabled={!text.trim()} onClick={go}>
        Guess
      </button>
    </div>
  );
}

function Question({
  q,
  locked,
  chosen = null,
  submitLabel,
  note,
  onAnswer,
}: {
  q: PublicQuestionCard;
  locked: boolean;
  chosen?: { option: number | null; text: string | null } | null;
  submitLabel: string;
  note?: string;
  onAnswer: (a: { option: number | null; text: string | null }) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    setPicked(null);
    setTyped("");
  }, [q.prompt]);
  return (
    <>
      <p className="pm-prompt">{q.prompt}</p>
      {q.grid && <pre className="pm-grid">{q.grid.join("\n")}</pre>}
      {q.options ? (
        <ol className="pm-options">
          {q.options.map((o, i) => {
            const on = locked ? chosen?.option === i : picked === i;
            return (
              <li key={i}>
                <button className={`pm-option ${on ? "is-chosen" : ""}`} disabled={locked} aria-pressed={on} onClick={() => setPicked(i)}>
                  <span className="pm-letter">{LETTERS[i]}</span>
                  <span>{o}</span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : locked ? (
        <p className="pm-locked">You said: {chosen?.text}</p>
      ) : (
        <input
          className="text-input pm-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          maxLength={60}
          placeholder="Your answer"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          onKeyDown={(e) => e.key === "Enter" && typed.trim() && onAnswer({ option: null, text: typed })}
        />
      )}
      {locked ? (
        q.options && <p className="pm-locked">Locked in. Wait for the others.</p>
      ) : (
        <button
          className="primary wax-button"
          disabled={q.options ? picked === null : !typed.trim()}
          onClick={() => {
            onAnswer(q.options ? { option: picked, text: null } : { option: null, text: typed });
            setTyped("");
          }}
        >
          {submitLabel}
        </button>
      )}
      {note && !locked && <p className="pm-small">{note}</p>}
    </>
  );
}

/** Your digit: press and hold to see it, so neighbours can't glance at it. Fog fades it away. */
function Digit({ digit, fog, solvedAt, now }: { digit: number; fog: boolean; solvedAt: number | null; now: number }) {
  const [shown, setShown] = useState(false);
  const faded = fog && solvedAt !== null && now > solvedAt + FOG_MS;
  if (faded) return <p className="panel-lead">Your digit has faded. Hope you called it out.</p>;
  return (
    <button
      className={`pm-digit ${shown ? "is-shown" : ""}`}
      onPointerDown={() => setShown(true)}
      onPointerUp={() => setShown(false)}
      onPointerLeave={() => setShown(false)}
      onPointerCancel={() => setShown(false)}
      onContextMenu={(e) => e.preventDefault()}
      aria-label="Press and hold to see your digit"
    >
      <span className="pm-digit-label">{shown ? "Your digit" : "Cracked it. Press and hold to see your digit"}</span>
      {shown && <span className="pm-digit-value">{digit}</span>}
      {fog && !shown && <span className="pm-small">It fades in a few seconds.</span>}
    </button>
  );
}

function Keypad({ digits, code, send }: { digits: number; code: string; send: (i: PlayInputInput) => Promise<void> }) {
  const type = (d: string) => code.length < digits && void send({ k: "code", code: code + d });
  return (
    <section className="pm-keypad" aria-label="Keyholder's keypad">
      <p className="pm-role">You're the Keyholder.</p>
      <p className="pm-code" aria-live="polite">
        {Array.from({ length: digits }, (_, i) => (
          <span key={i}>{code[i] ?? "·"}</span>
        ))}
      </p>
      <div className="pm-keys">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} className="pm-key" onClick={() => type(d)}>
            {d}
          </button>
        ))}
        <button className="pm-key is-quiet" onClick={() => void send({ k: "code", code: code.slice(0, -1) })} aria-label="Delete">
          ⌫
        </button>
        <button className="pm-key" onClick={() => type("0")}>
          0
        </button>
        <button className="pm-key is-quiet" onClick={() => void send({ k: "code", code: "" })}>
          Clear
        </button>
      </div>
      <button className="primary wax-button" disabled={code.length !== digits} onClick={() => void send({ k: "unlock" })}>
        Unlock
      </button>
      <p className="pm-small">You only get one go. Unlock when the room agrees.</p>
    </section>
  );
}

function NumberEntry({ unit, onSubmit }: { unit?: string; onSubmit: (n: number) => void }) {
  const [text, setText] = useState("");
  const value = Number(text.replace(/[,\s£]/g, ""));
  const valid = text.trim() !== "" && Number.isFinite(value);
  return (
    <>
      <div className="pm-number">
        <input
          className="text-input pm-input"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Your number"
          enterKeyHint="done"
          onKeyDown={(e) => e.key === "Enter" && valid && onSubmit(value)}
        />
        {unit && <span className="pm-unit">{unit}</span>}
      </div>
      <button className="primary wax-button" disabled={!valid} onClick={() => onSubmit(value)}>
        Lock in my guess
      </button>
    </>
  );
}

function useNow(offset: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  return now + offset;
}
